import { logger } from "@platform/browser-logger";

import useToastNotificationService from "./useToastNotificationService";

interface CopyToClipboardOptions {
  /** When present, writes both an HTML and a text/plain flavor via
   * `ClipboardItem` so a rich-text target (e.g. an Outlook draft) preserves
   * formatting on paste. Falls back to plain `writeText` when the browser
   * lacks `ClipboardItem` support. */
  html?: string;
}

const writeToClipboard = (
  text: string,
  html: string | undefined,
): Promise<void> => {
  if (
    html &&
    navigator.clipboard.write &&
    typeof ClipboardItem !== "undefined"
  ) {
    return navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": new Blob([text], { type: "text/plain" }),
        "text/html": new Blob([html], { type: "text/html" }),
      }),
    ]);
  }

  return navigator.clipboard.writeText(text);
};

const useCopyTextToClipboard = (
  params: {
    autoHideDuration?: number;
  } = {},
): ((
  text: string,
  message: string,
  error: string,
  options?: CopyToClipboardOptions,
) => Promise<void>) => {
  const { autoHideDuration } = params;
  const { createToastNotification } = useToastNotificationService();

  const copyToClipboard = async (
    text: string,
    message: string,
    error: string,
    options?: CopyToClipboardOptions,
  ): Promise<void> => {
    try {
      await writeToClipboard(text, options?.html);
      createToastNotification({
        message,
        severity: "info",
        autoHideDuration,
      });
    } catch (err) {
      logger.error("Failed to copy to clipboard", err);
      createToastNotification({
        message: error,
        severity: "error",
        autoHideDuration,
      });
    }
  };

  return copyToClipboard;
};

export { useCopyTextToClipboard };
